import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collaborationConfigsApi } from '@/services/api';
import type { CreateCollaborationConfigInput, UpdateCollaborationConfigInput } from '@/types';

export const useCollaborationConfigs = (page = 1, limit = 20, filters?: { search?: string }) => {
  return useQuery({
    queryKey: ['collaboration-configs', page, limit, filters],
    queryFn: () => collaborationConfigsApi.getAll(page, limit, filters).then(res => res.data),
  });
};

export const useCollaborationConfig = (id: string | undefined) => {
  return useQuery({
    queryKey: ['collaboration-config', id],
    queryFn: () => collaborationConfigsApi.getById(id!).then(res => res.data.data),
    enabled: !!id,
  });
};

export const useCreateCollaborationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCollaborationConfigInput) =>
      collaborationConfigsApi.create(data).then(res => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-configs'] });
    },
  });
};

export const useUpdateCollaborationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCollaborationConfigInput }) =>
      collaborationConfigsApi.update(id, data).then(res => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-configs'] });
      queryClient.invalidateQueries({ queryKey: ['collaboration-config', variables.id] });
    },
  });
};

export const useDeleteCollaborationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => collaborationConfigsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collaboration-configs'] });
    },
  });
};
